import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Alert, Icon, Tooltip, Typography } from 'shared/ui'
import { PromptFieldText } from 'entities/Chatbot/utils/constants'
import { type IParamsEnhancePrompt } from 'entities/Chatbot'
import { EnhancePrompt } from 'entities/AIAgent/actions/enhancePrompt/EnhancePrompt'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { requestTextingAgentEnhance } from 'entities/AIAgent/Texting/helpers/requestTextingAgentEnhance'
import { type AdvancedSettingsStore } from 'pages/chatbot/pages/agentBookAppointment/store/advancedSettingsStore'
import { PromptField } from 'pages/chatbot/ui/PromptFields/PromptField'
import styles from './styles.module.scss'

export type IPromptSettingsProps = {
  settings: AdvancedSettingsStore
  promptData: IParamsEnhancePrompt
}

export const PromptSettings = observer(({ settings, promptData }: IPromptSettingsProps) => {
  const entities = useEntitiesContext()
  const { promptLengthMax, instructions, promptLength, setInstructions, errorsMap, reset } =
    settings.promptSettingsStore

  const promptsLengthError = errorsMap.get('promptsLength')
  const instructionError = errorsMap.get('instructions')

  const setInstructionsError = (error: string) => {
    errorsMap.set('instructions', error)
  }

  return (
    <div className={styles.promptSettingsWrapper}>
      <div className={styles.promptTitleWrapper}>
        <Typography variant={'body-lg-medium'} ariaLabel={'AgentPrompt'}>
          Agent prompt
        </Typography>
        <Typography
          variant={'body-md-regular'}
          ariaLabel={'AgentPromptLength'}
          color={'var(--content-primary-disabled)'}
          className={styles.promptSizeTitle}
        >
          <Tooltip
            label={
              'The prompt, including all sections, is limited to 7,000 characters. Keep it concise for the best performance.'
            }
            placement={'top'}
            breakWord
          >
            <Icon icon='question' fontSize={14} />
          </Tooltip>
          <span
            className={classNames(styles.promptSize, {
              [styles.promptSizeError]: !!promptsLengthError,
            })}
          >{`${promptLength} of ${promptLengthMax}`}</span>
        </Typography>
      </div>
      {promptsLengthError && (
        <Alert
          item={{
            type: 'error',
            desc: promptsLengthError,
          }}
          marginBottom={20}
        />
      )}
      <PromptField
        value={instructions}
        error={instructionError}
        onChange={setInstructions}
        resetField={reset}
        questionTooltipProps={{
          label: PromptFieldText.instructions.tooltipLabel,
          breakWord: true,
        }}
        {...PromptFieldText.instructions}
        maxHeight={502}
        rightTopAction={
          <EnhancePrompt
            prompt={instructions}
            onAcceptPrompt={setInstructions}
            setError={setInstructionsError}
            requestEnhance={requestTextingAgentEnhance(entities, promptData)}
          />
        }
      />
    </div>
  )
})
